
import VendorCard from "@/components/shop/VendorCard";
import { Card } from "@/components/ui/card";
import { Vendor } from "@/types/shop";
import { mockVendors } from "@/utils/mockProducts";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";

interface VendorListProps {
  vendors: Vendor[] | null;
  isLoading: boolean;
  viewMode: "grid" | "list";
}

const VendorList = ({ vendors, isLoading, viewMode }: VendorListProps) => {
  const isMobile = useIsMobile();
  // Use mock data if vendors array is empty
  const displayVendors = (!vendors || vendors.length === 0) ? mockVendors : vendors;
  
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" aria-busy="true" aria-live="polite">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="h-[300px] animate-pulse bg-gray-100 overflow-hidden" tabIndex={-1}>
            <div className="h-1/3 bg-gray-200"></div>
            <div className="p-6 space-y-3">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <div className="space-y-2 pt-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
                <Skeleton className="h-3 w-4/6" />
              </div>
              <div className="pt-4 flex gap-2">
                <Skeleton className="h-10 w-1/2" />
                <Skeleton className="h-10 w-1/2" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!displayVendors?.length) {
    return (
      <div className="col-span-full text-center py-10" role="alert">
        <p className="text-muted-foreground">No vendors found</p>
      </div>
    );
  }

  return (
    <div 
      className={`grid grid-cols-1 ${
        viewMode === "grid" 
          ? isMobile 
            ? "gap-4" 
            : "md:grid-cols-2 lg:grid-cols-3 gap-6"
          : "md:grid-cols-1 gap-4"
      }`}
      role="list"
      aria-label="Vendors list"
    >
      {displayVendors.map((vendor) => (
        <div key={vendor.id} role="listitem">
          <VendorCard vendor={vendor} viewMode={viewMode} />
        </div>
      ))}
    </div>
  );
};

export default VendorList;
